import React, {useState, useEffect} from 'react';
import analytics from '@react-native-firebase/analytics';
import {ActivityIndicator, Alert, Modal, Platform} from 'react-native';
import { useNavigation, useRoute} from '@react-navigation/native';
import {SafeAreaView} from 'react-native-safe-area-context';
import DeviceInfo from 'react-native-device-info';
import {RFValue} from 'react-native-responsive-fontsize';
import {useTheme} from 'styled-components/native';

import {Button} from '../../../components/Interface/Forms/Buttons/Button';
import {ButtonTimer} from '../../../components/Interface/Forms/Buttons/ButtonTimer';
import {PropostaDTO} from '../../../dtos/EmprestimoDTO';
import {
  buscarResumoAssinatura,
  solicitarTokenAssinatura,
  confirmarAssinatura,
  AssinaturaResumoResponse,
} from '../../../services/creditoMova';

import {
  Container,
  Content,
  Banner,
  BackButton,
  BackIcon,
  BannerIconWrapper,
  BannerIcon,
  BannerTitle,
  BannerSubtitle,
  SummaryCard,
  SummaryRow,
  SummaryLabel,
  SummaryValue,
  Divider,
  CheckboxRow,
  CheckboxBox,
  CheckboxIcon,
  CheckboxText,
  OtpSection,
  OtpDescription,
  OtpNoticeCard,
  OtpNoticeIcon,
  OtpNoticeText,
  OtpCodeContainer,
  OtpTokenInput,
  OtpErrorText,
  Footer,
  SuccessContainer,
  SuccessIconWrapper,
  SuccessIconCheck,
  SuccessTitle,
  SuccessSubtitle,
  DisclaimerText,
  ModalOverlay,
  ModalCard,
  ModalIconWrapper,
  ModalIcon,
  ModalTitle,
  ModalText,
  ModalButtonRow,
  ModalCancelButton,
  ModalCancelText,
  ModalConfirmButton,
  ModalConfirmText,
} from './styles';
import { useLocalization } from '../../../context/localization';
import {EmprestimoEligibilityGate} from '../../../components/shared/EmprestimoEligibilityGate';

type RouteParams = {
  proposta: PropostaDTO;
};

type Step = 'loading' | 'termos' | 'otp' | 'success';

const COUNTDOWN_SECONDS = 2 * 60; // 2 minutos

function formatValor(value: number): string {
  return value.toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  });
}

function formatRate(rate: number): string {
  return `${(rate * 100).toFixed(2)}%`;
}

function formatDate(dateStr: string): string {
  if (!dateStr) {
    return '';
  }
  const [year, month, day] = dateStr.split('-');
  return `${day}/${month}/${year}`;
}

export function Assinatura() {
  const navigation = useNavigation();
  const route = useRoute();
  const theme = useTheme();

  const {position} = useLocalization();

  const {proposta} = route.params as RouteParams;

  // Resumo data from backend
  const [resumo, setResumo] = useState<AssinaturaResumoResponse | null>(null);

  // Step control
  const [step, setStep] = useState<Step>('loading');

  // Step 1: Termos
  const [accepted, setAccepted] = useState(false);
  const [loadingNext, setLoadingNext] = useState(false);

  // Step 2: OTP
  const [otpValue, setOtpValue] = useState('');
  const [otpLoading, setOtpLoading] = useState(false);
  const [otpError, setOtpError] = useState('');
  const [secondsAmount, setSecondsAmount] = useState(COUNTDOWN_SECONDS);
  const [resendEnabled, setResendEnabled] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);

  // Dados financeiros do resumo (ou fallback da proposta local)
  const financial = resumo?.financial;
  const signature = resumo?.signature;

  const termsContent =
    signature?.terms_content || proposta.terms_content || '';

  const disbursementDate =
    resumo?.mova?.disbursement_target_date ||
    proposta.disbursement_target_date;

  // Buscar resumo ao montar
  useEffect(() => {
    async function fetchResumo() {
      try {
        const response = await buscarResumoAssinatura(proposta.operacao_id);
        setResumo(response);

        if (
          response.status === 'em_assinatura' ||
          response.mova?.proposal_status === 'EM ASSINATURA'
        ) {
          setStep('termos');
        } else {
          Alert.alert(
            'Operação indisponível',
            `Esta operação está com status "${response.status}" e não pode ser assinada no momento.`,
            [{text: 'OK', onPress: () => navigation.goBack()}],
          );
        }
      } catch (error: any) {
        const status = error?.response?.status;
        if (status === 422) {
          Alert.alert(
            'Erro',
            'Operação não encontrada ou inválida.',
            [{text: 'OK', onPress: () => navigation.goBack()}],
          );
        } else {
          Alert.alert(
            'Erro',
            'Não foi possível carregar os dados da assinatura. Tente novamente.',
            [{text: 'OK', onPress: () => navigation.goBack()}],
          );
        }
      }
    }
    fetchResumo();
  }, [proposta.operacao_id, navigation]);

  // Analytics: registra visualização de cada etapa (termos, otp, success)
  useEffect(() => {
    if (step === 'loading') {
      return;
    }
    analytics().logEvent('emprestimo_tela_assinatura', {
      etapa: step,
    });
  }, [step]);

  // Timer OTP
  useEffect(() => {
    if (step !== 'otp') {
      return;
    }

    if (secondsAmount === 0) {
      setResendEnabled(true);
      return;
    }

    const timer = setTimeout(() => {
      setSecondsAmount(prev => prev - 1);
    }, 1000);

    return () => clearTimeout(timer);
  }, [secondsAmount, step]);

  const handleSolicitarToken = async () => {
    analytics().logEvent('emprestimo_clicou_enviar_codigo');
    setLoadingNext(true);
    try {
      const response = await solicitarTokenAssinatura({
        operacao_id: proposta.operacao_id,
        token_method: ['sms'],
      });

      if (response.ok) {
        setStep('otp');
        setOtpValue('');
        setOtpError('');
        setSecondsAmount(COUNTDOWN_SECONDS);
        setResendEnabled(false);
      } else {
        Alert.alert(
          'Erro',
          'Não foi possível enviar o código. Tente novamente.',
        );
      }
    } catch (error: any) {
      const status = error?.response?.status;
      if (status === 422) {
        Alert.alert('Erro', 'Operação inválida. Verifique os dados.');
      } else if (status === 502) {
        Alert.alert(
          'Erro',
          'Serviço temporariamente indisponível. Tente novamente em alguns minutos.',
        );
      } else {
        Alert.alert(
          'Erro',
          'Não foi possível enviar o código. Tente novamente.',
        );
      }
    } finally {
      setLoadingNext(false);
    }
  };

  const handleResendToken = async () => {
    analytics().logEvent('emprestimo_reenviou_codigo_otp');
    setResendEnabled(false);
    setSecondsAmount(COUNTDOWN_SECONDS);
    setOtpValue('');
    setOtpError('');
    try {
      await solicitarTokenAssinatura({
        operacao_id: proposta.operacao_id,
        token_method: ['sms'],
      });
    } catch (error) {
      Alert.alert('Erro', 'Não foi possível reenviar o código.');
    }
  };

  const handleConfirmarAssinatura = async (token: string) => {
    setOtpLoading(true);
    setOtpError('');
    try {
      // Coletar dados opcionais do dispositivo
      const deviceManufacturer = await DeviceInfo.getManufacturer();
      const deviceModel = DeviceInfo.getModel();
      const deviceOs = Platform.OS === 'ios' ? 'iOS' : 'Android';
      const deviceOsVersion = DeviceInfo.getSystemVersion();

      const response = await confirmarAssinatura({
        operacao_id: proposta.operacao_id,
        token_number: token,
        terms_content: termsContent,
        device_manufacturer: deviceManufacturer,
        device_model: deviceModel,
        device_os: deviceOs,
        device_os_version: deviceOsVersion,
        geolocation:position?.latitude + ',' + position?.longitude || undefined,
      });

      if (response.ok) {
        // Atualizar status da proposta no AsyncStorage


        setStep('success');
      } else {
        setOtpError('Não foi possível assinar. Tente novamente.');
        setOtpValue('');
      }
    } catch (error: any) {
      const status = error?.response?.status;
      if (status === 400) {
        setOtpError('Código inválido ou expirado. Tente novamente.');
        setOtpValue('');
      } else if (status === 422) {
        setOtpError('Operação inválida.');
      } else if (status === 502) {
        setOtpError(
          'Não foi possível assinar agora. Confira o código e tente novamente.',
        );
      } else {
        setOtpError('Erro ao assinar. Verifique sua conexão.');
      }
    } finally {
      setOtpLoading(false);
    }
  };

  const handleGoHome = () => {
    navigation.navigate('Emprestimo', {initialTab: 'emprestimos'});

  };

  // Valores para exibição (do resumo do backend, fallback p/ proposta local)
  const displayValor = financial?.operation_net_amount ?? proposta.valor;
  const displayParcelas = financial?.installments_number ?? proposta.parcelas;
  const displayValorParcela = financial?.installment_amount ?? proposta.valor_parcela;
  const displayTaxaMensal = financial?.interest_monthly_rate ?? proposta.taxa_mensal;
  const displayTotal =
    financial
      ? financial.installment_amount * financial.installments_number
      : proposta.total;
  const displayFirstInstallment =
    financial?.first_installment_date ?? proposta.first_installment_date;
  const displayLastInstallment =
    financial?.last_installment_date ?? proposta.last_installment_date;
  const displayDocumentDescription =
    signature?.document_description ?? proposta.document_description;

  const displayGrossAmount = financial?.operation_gross_amount ?? proposta.operation_gross_amount ?? 0;
  const displayIof = financial?.iof_total_value ?? proposta.iof_total_value ?? 0;
  const displayCetAnual = financial?.gross_costs_annual_rate ?? proposta.taxa_anual ?? 0;

  // ─── LOADING ───
  if (step === 'loading') {
    return (
      <Container
        style={{
          alignItems: 'center',
          justifyContent: 'center',
        }}>
        <ActivityIndicator size="large" color={theme.colors.loan_banner_bg} />
      </Container>
    );
  }

  // ─── TELA DE SUCESSO ───
  if (step === 'success') {
    return (
      <Container>
        <SuccessContainer>
          <SafeAreaView
            style={{alignItems: 'center', flex: 1, justifyContent: 'center'}}>
            <SuccessIconWrapper>
              <SuccessIconCheck name="check" />
            </SuccessIconWrapper>
            <SuccessTitle>Assinatura enviada!</SuccessTitle>
            <SuccessSubtitle>
              Sua assinatura foi enviada com sucesso.{'\n'}Aguarde a confirmação
              — você será notificado pelo aplicativo.
            </SuccessSubtitle>


          </SafeAreaView>
        </SuccessContainer>
        <Footer>
          <SafeAreaView edges={['bottom']}>
            <Button title="Ir para o início" onPress={handleGoHome} enabled />
          </SafeAreaView>
        </Footer>
      </Container>
    );
  }

  // ─── TELA DE OTP ───
  if (step === 'otp') {
    return (
      <Container>
        <Content>
          <Banner>
            <SafeAreaView style={{alignItems: 'center', width: '100%'}}>
              <BackButton
                activeOpacity={0.7}
                onPress={() => setStep('termos')}>
                <BackIcon name="chevron-left" />
              </BackButton>

              <BannerIconWrapper>
                <BannerIcon name="smartphone" />
              </BannerIconWrapper>
              <BannerTitle>Verificação de segurança</BannerTitle>
              <BannerSubtitle>
                Digite o código enviado por SMS{'\n'}para confirmar a
                assinatura.
              </BannerSubtitle>
            </SafeAreaView>
          </Banner>

          <OtpSection>

            <OtpDescription>
              Enviamos um código para o número cadastrado.
              Digite-o abaixo para assinar o contrato:
            </OtpDescription>

            <OtpNoticeCard>
              <OtpNoticeIcon name="alert-circle" />
              <OtpNoticeText>
                O código pode conter letras maiúsculas e minúsculas. Digite-o exatamente como recebido.
              </OtpNoticeText>
            </OtpNoticeCard>

            <OtpCodeContainer>
              <OtpTokenInput
                value={otpValue}
                onChangeText={setOtpValue}
                autoFocus
                autoCorrect={false}
                autoCapitalize="characters"
                placeholder="Digite o código"
                placeholderTextColor={theme.colors.placeholder}
                editable={!otpLoading}
                textContentType="oneTimeCode"
                autoComplete="sms-otp"
              />
            </OtpCodeContainer>

            {otpLoading && (
              <ActivityIndicator
                size="small"
                color={theme.colors.loan_banner_bg}
                style={{marginTop: 8}}
              />
            )}

            {!!otpError && <OtpErrorText>{otpError}</OtpErrorText>}

            <Button
              title="Confirmar assinatura"
              onPress={() => setShowAuthModal(true)}
              enabled={otpValue.trim().length > 0 && !otpLoading}
            />

            <ButtonTimer
              timer={secondsAmount}
              onPress={handleResendToken}
              disabled={!resendEnabled}
              style={{
                opacity: resendEnabled ? 1 : 0.4,
                marginTop: RFValue(16),
              }}
            />
          </OtpSection>
        </Content>

        <Modal
          visible={showAuthModal}
          transparent
          animationType="fade"
          onRequestClose={() => setShowAuthModal(false)}>
          <ModalOverlay>
            <ModalCard>
              <ModalIconWrapper>
                <ModalIcon name="alert-circle" />
              </ModalIconWrapper>
              <ModalTitle>Autorização de débito</ModalTitle>
              <ModalText>
                Autorizo, de forma prévia, expressa e irrevogável (salvo
                quitação integral), o Baratão Combustíveis a realizar débitos no
                cartão de crédito cadastrado para liquidação de valores em aberto
                decorrentes de empréstimos contratados, em caso de inadimplência
                na data de vencimento, incluindo principal, encargos e eventuais
                penalidades previstas.
              </ModalText>
              <ModalButtonRow>
                <ModalCancelButton
                  activeOpacity={0.7}
                  onPress={() => setShowAuthModal(false)}>
                  <ModalCancelText>Cancelar</ModalCancelText>
                </ModalCancelButton>
                <ModalConfirmButton
                  activeOpacity={0.7}
                  onPress={() => {
                    analytics().logEvent('emprestimo_confirmou_autorizacao_debito');
                    setShowAuthModal(false);
                    handleConfirmarAssinatura(otpValue);
                  }}>
                  <ModalConfirmText>Confirmar</ModalConfirmText>
                </ModalConfirmButton>
              </ModalButtonRow>
            </ModalCard>
          </ModalOverlay>
        </Modal>
      </Container>
    );
  }

  // ─── TELA DE TERMOS E ACEITE (Step 1) ───
  const documentDescription =
    displayDocumentDescription || 'Contrato de crédito pessoal';
  const canProceed =
    accepted;

  return (
    <Container>
      <EmprestimoEligibilityGate />
      <Content>
        <Banner>
          <SafeAreaView style={{alignItems: 'center', width: '100%'}}>
            <BackButton
              activeOpacity={0.7}
              onPress={() => navigation.goBack()}>
              <BackIcon name="chevron-left" />
            </BackButton>

            <BannerIconWrapper>
              <BannerIcon name="file-text" />
            </BannerIconWrapper>
            <BannerTitle>Assinar contrato</BannerTitle>
            <BannerSubtitle>
              {documentDescription}
              {'\n'}Confira os dados e concorde com os termos para assinar.
            </BannerSubtitle>
          </SafeAreaView>
        </Banner>

        <SummaryCard>

          <SummaryRow>
            <SummaryLabel>Valor solicitado</SummaryLabel>
            <SummaryValue>{formatValor(displayValor)}</SummaryValue>
          </SummaryRow>

          {displayGrossAmount > 0 && (
            <SummaryRow>
              <SummaryLabel>Valor Financiado</SummaryLabel>
              <SummaryValue>{formatValor(displayGrossAmount)}</SummaryValue>
            </SummaryRow>
          )}
          {displayIof > 0 && (
            <SummaryRow>
              <SummaryLabel>Impostos (IOF)</SummaryLabel>
              <SummaryValue>{formatValor(displayIof)}</SummaryValue>
            </SummaryRow>
          )}

          <Divider />
          <SummaryRow>
            <SummaryLabel>Você irá pagar</SummaryLabel>
            <SummaryValue>
              {displayParcelas}x de {formatValor(displayValorParcela)} fixas
            </SummaryValue>
          </SummaryRow>
          {displayFirstInstallment ? (
            <SummaryRow>
              <SummaryLabel>Data do 1º vencimento</SummaryLabel>
              <SummaryValue>
                {formatDate(displayFirstInstallment)}
              </SummaryValue>
            </SummaryRow>
          ) : null}
          <SummaryRow>
            <SummaryLabel>Taxa mensal</SummaryLabel>
            <SummaryValue>{formatRate(displayTaxaMensal)}</SummaryValue>
          </SummaryRow>
          {displayLastInstallment ? (
            <SummaryRow>
              <SummaryLabel>Última parcela</SummaryLabel>
              <SummaryValue>
                {formatDate(displayLastInstallment)}
              </SummaryValue>
            </SummaryRow>
          ) : null}
          <Divider />
          <SummaryRow style={{marginBottom: 0}}>
            <SummaryLabel>Total a pagar</SummaryLabel>
            <SummaryValue>{formatValor(displayTotal)}</SummaryValue>
          </SummaryRow>
        </SummaryCard>

        <DisclaimerText>
          A taxa de juros aplicada foi de {formatRate(displayTaxaMensal)} ao mês
          {displayTotal > 0
            ? ` e a CET (Custo Efetivo Total) ficou em ${formatRate(
                displayCetAnual,
              )} ao ano.`
            : '.'}
        </DisclaimerText>

        {disbursementDate ? (
          <SummaryCard style={{marginTop: RFValue(12)}}>
            <SummaryRow style={{marginBottom: 0}}>
              <SummaryLabel>Previsão de desembolso</SummaryLabel>
              <SummaryValue>{formatDate(disbursementDate)}</SummaryValue>
            </SummaryRow>
          </SummaryCard>
        ) : null}

        <CheckboxRow
          activeOpacity={0.7}
          onPress={() => setAccepted(prev => !prev)}>
          <CheckboxBox checked={accepted}>
            {accepted && <CheckboxIcon name="check" />}
          </CheckboxBox>
          <CheckboxText>{termsContent}</CheckboxText>
        </CheckboxRow>
      </Content>

      <Footer>
        <SafeAreaView edges={['bottom']}>
          {loadingNext ? (
            <ActivityIndicator size="large" color={theme.colors.title} />
          ) : (
            <Button
              title="Enviar código"
              onPress={handleSolicitarToken}
              enabled={canProceed}
            />
          )}
        </SafeAreaView>
      </Footer>
    </Container>
  );
}